import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerHeader from '../components/CustomerHeader';
import './Checkout.css';
import { supabase } from '../config/supabase';
import { getDeliverySettings } from '../utils/deliverySettings';

const Checkout = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [deliverySettings, setDeliverySettings] = useState({
    deliveryFee: 99,
    freeDeliveryAbove: 800,
    minOrderAmount: 300
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    deliveryAddress: 'DHA Phase 6, Lahore',
    deliveryInstructions: '',
    paymentMethod: 'cash', // 'cash' or 'card'
    cardNumber: '',
    cardName: '',
    cardExpiry: '',
    cardCVC: ''
  });

  // Load cart, customer data, and delivery settings
  useEffect(() => {
    loadCart();
    checkAuth();
    loadDeliverySettings();
    // Listen for cart updates
    window.addEventListener('storage', loadCart);
    window.addEventListener('cartUpdated', loadCart);
    return () => {
      window.removeEventListener('storage', loadCart);
      window.removeEventListener('cartUpdated', loadCart);
    };
  }, []);

  const loadDeliverySettings = async () => {
    try {
      const { data, error } = await getDeliverySettings();
      if (error) {
        console.error('Error loading delivery settings:', error);
        return;
      }
      if (data) {
        setDeliverySettings({
          deliveryFee: parseFloat(data.delivery_fee) || 99,
          freeDeliveryAbove: parseFloat(data.free_delivery_above) || 800,
          minOrderAmount: parseFloat(data.min_order_amount) || 300
        });
      }
    } catch (error) {
      console.error('Error loading delivery settings:', error);
    }
  };

  const loadCart = () => {
    try {
      const savedCart = localStorage.getItem('restaurantCart');
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      } else {
        setCart([]);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      setCart([]);
    }
  };

  const checkAuth = () => {
    try {
      const customerData = localStorage.getItem('customer');
      if (customerData) {
        const customerObj = JSON.parse(customerData);
        setIsLoggedIn(true);
        setCustomer(customerObj);
        // Pre-fill form with customer data
        setFormData(prev => ({
          ...prev,
          name: customerObj.name || '',
          email: customerObj.email || '',
          phone: customerObj.phone || '',
          deliveryAddress: customerObj.delivery_address || 'DHA Phase 6, Lahore'
        }));
      }
    } catch (error) {
      console.error('Error loading customer data:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getDeliveryFee = () => {
    const subtotal = getSubtotal();
    return subtotal >= deliverySettings.freeDeliveryAbove ? 0 : deliverySettings.deliveryFee;
  };

  const getTotal = () => {
    return getSubtotal() + getDeliveryFee();
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      alert('Please enter your name');
      return false;
    }
    if (!formData.email.trim()) {
      alert('Please enter your email');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Please enter a valid email address');
      return false;
    }
    if (!formData.phone.trim()) {
      alert('Please enter your phone number');
      return false;
    }
    if (!formData.deliveryAddress.trim()) {
      alert('Please enter delivery address');
      return false;
    }
    return true;
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) {
      alert('Your cart is empty');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Create order in Supabase
      const orderData = {
        customer_id: customer?.id || null,
        customer_name: formData.name.trim(),
        customer_email: formData.email.toLowerCase().trim(),
        customer_phone: formData.phone.trim() || null,
        delivery_address: formData.deliveryAddress.trim(),
        delivery_instructions: formData.deliveryInstructions.trim() || null,
        payment_method: formData.paymentMethod,
        subtotal: parseFloat(getSubtotal().toFixed(2)),
        delivery_fee: parseFloat(getDeliveryFee().toFixed(2)),
        total: parseFloat(getTotal().toFixed(2)),
        status: 'pending',
        items: cart.map(item => ({
          menu_item_id: item.id,
          name: item.name,
          price: parseFloat(item.price),
          quantity: item.quantity,
          // Include customization details
          selectedSize: item.selectedSize || null,
          selectedSpice: item.selectedSpice || null,
          selectedAddons: item.selectedAddons || [], // Array of addon objects
          specialInstructions: item.specialInstructions || null,
          // Store final price per item (includes size and addon prices)
          finalPrice: item.finalPrice ? parseFloat(item.finalPrice) : parseFloat(item.price) * item.quantity
        }))
      };

      // Insert order into orders table
      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert([orderData])
        .select()
        .single();

      if (orderError) {
        console.error('Order creation error:', orderError);
        throw orderError;
      }

      // Clear cart
      localStorage.removeItem('restaurantCart');
      window.dispatchEvent(new Event('cartUpdated'));

      // Redirect to order confirmation
      navigate(`/order-confirmation/${order.id}`);
    } catch (error) {
      console.error('Error placing order:', error);
      alert('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Redirect if cart is empty (only on initial load)
  useEffect(() => {
    const checkCart = () => {
      const savedCart = localStorage.getItem('restaurantCart');
      if (!savedCart || JSON.parse(savedCart).length === 0) {
        navigate('/cart');
      }
    };
    checkCart();
  }, [navigate]);

  return (
    <div className="checkout-page">
      <CustomerHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        cartItemCount={getCartItemCount()}
        onCartClick={() => navigate('/cart')}
      />

      {/* Page Header */}
      <div className="checkout-page-header">
        <div className="checkout-page-header-container">
          <button
            className="checkout-page-back-btn"
            onClick={() => navigate('/cart')}
          >
            <span className="checkout-page-back-icon">←</span>
            <span className="checkout-page-back-text">Back to Cart</span>
          </button>
          <h1 className="checkout-page-title">Checkout</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="checkout-page-main">
        <div className="checkout-page-container">
          <div className="checkout-page-content">
            {/* Left Column - Forms */}
            <div className="checkout-page-forms-section">
              {/* Customer Information */}
              <div className="checkout-page-section">
                <h2 className="checkout-page-section-title">Customer Information</h2>
                <div className="checkout-page-form">
                  <div className="checkout-page-form-row">
                    <div className="checkout-page-form-group">
                      <label className="checkout-page-label">
                        Full Name <span className="checkout-page-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        className="checkout-page-input"
                        placeholder="Enter your full name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="checkout-page-form-group">
                      <label className="checkout-page-label">
                        Email <span className="checkout-page-required">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        className="checkout-page-input"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="checkout-page-form-group">
                    <label className="checkout-page-label">
                      Phone Number <span className="checkout-page-required">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className="checkout-page-input"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="checkout-page-section">
                <h2 className="checkout-page-section-title">Delivery Address</h2>
                <div className="checkout-page-form">
                  <div className="checkout-page-form-group">
                    <label className="checkout-page-label">
                      Address <span className="checkout-page-required">*</span>
                    </label>
                    <textarea
                      name="deliveryAddress"
                      className="checkout-page-textarea"
                      placeholder="Enter your delivery address"
                      rows={3}
                      value={formData.deliveryAddress}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="checkout-page-form-group">
                    <label className="checkout-page-label">Delivery Instructions (Optional)</label>
                    <textarea
                      name="deliveryInstructions"
                      className="checkout-page-textarea"
                      placeholder="Any special instructions for delivery?"
                      rows={2}
                      value={formData.deliveryInstructions}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="checkout-page-section">
                <h2 className="checkout-page-section-title">Payment Method</h2>
                <div className="checkout-page-form">
                  <div className="checkout-page-payment-options">
                    <label className="checkout-page-payment-option">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cash"
                        checked={formData.paymentMethod === 'cash'}
                        onChange={handleChange}
                      />
                      <span className="checkout-page-payment-label">
                        <span className="checkout-page-payment-icon">💵</span>
                        Cash on Delivery
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="checkout-page-summary-section">
              <div className="checkout-page-summary-card">
                <h2 className="checkout-page-summary-title">Order Summary</h2>

                {/* Order Items */}
                <div className="checkout-page-items-list">
                  {cart.map((item) => (
                    <div key={item.id} className="checkout-page-item">
                      <div className="checkout-page-item-info">
                        <h4 className="checkout-page-item-name">{item.name}</h4>
                        <span className="checkout-page-item-quantity">
                          Qty: {item.quantity}
                        </span>
                      </div>
                      <div className="checkout-page-item-price">
                        Rs. {(item.price * item.quantity).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Order Totals */}
                <div className="checkout-page-totals">
                  <div className="checkout-page-total-row">
                    <span className="checkout-page-total-label">Subtotal</span>
                    <span className="checkout-page-total-value">
                      Rs. {getSubtotal().toFixed(2)}
                    </span>
                  </div>
                  <div className="checkout-page-total-row">
                    <span className="checkout-page-total-label">Delivery Fee</span>
                    <span className="checkout-page-total-value">
                        {getDeliveryFee() === 0 ? (
                          <span className="checkout-page-free">Free</span>
                        ) : (
                          `Rs. ${getDeliveryFee().toFixed(2)}`
                        )}
                      </span>
                    </div>
                    {getSubtotal() < deliverySettings.freeDeliveryAbove && (
                      <div className="checkout-page-delivery-note">
                        Add Rs. {(deliverySettings.freeDeliveryAbove - getSubtotal()).toFixed(2)} more for free delivery!
                      </div>
                    )}
                </div>

                {/* Grand Total */}
                <div className="checkout-page-grand-total">
                  <span className="checkout-page-grand-total-label">Total</span>
                  <span className="checkout-page-grand-total-amount">
                    Rs. {getTotal().toFixed(2)}
                  </span>
                </div>

                {/* Place Order Button */}
                <button
                  className="checkout-page-place-order-btn"
                  onClick={handlePlaceOrder}
                  disabled={loading}
                >
                  {loading ? 'Placing Order...' : 'Place Order'}
                </button>

                {/* Security Note */}
                <p className="checkout-page-security-note">
                  🔒 Your payment information is secure and encrypted
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;

